import React, { useRef, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Input,
    Space,
    Typography,
    message,
} from 'antd';
import { CheckCircleOutlined, CopyOutlined, KeyOutlined, PlayCircleOutlined, YoutubeOutlined } from '@ant-design/icons';
import { PageContainer } from '@ant-design/pro-components';
import axiosApiInstance from '../../api/axiosClient';
import { auth } from '../../config/firebase';

type PlaylistStatus = {
    id: string;
    title: string;
    channel_id: string;
    channel_title: string;
    item_count: number;
    readable: boolean;
    error?: string;
};

type ChannelInfo = {
    id: string;
    title: string;
};

type CheckResult = {
    source: PlaylistStatus;
    dest: PlaylistStatus;
    auth_required: boolean;
    auth_channel?: ChannelInfo;
    dest_owned: boolean;
    message: string;
};

type DuplicateInfo = {
    video_id: string;
    title: string;
    source_count: number;
    dest_count: number;
};

type UnavailableInfo = {
    video_id: string;
    title: string;
    reason: string;
};

type PlaylistItem = {
    video_id: string;
    title: string;
};

type PreviewResult = {
    source: PlaylistStatus;
    dest: PlaylistStatus;
    auth_channel: ChannelInfo;
    plan: {
        to_insert: PlaylistItem[];
        skipped_existing: number;
        skipped_existing_ids: string[];
        skipped_unavailable: number;
        source_count: number;
        dest_count: number;
        would_exceed_cap: boolean;
    };
    duplicates: DuplicateInfo[];
    unavailable: UnavailableInfo[];
    duplicate_extra_entries: number;
};

type LogLine = {
    time?: string;
    type: string;
    message: string;
    video_id?: string;
    title?: string;
};

const apiError = (err: any): string =>
    err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Request failed';

const logColor = (type: string): string => {
    switch (type) {
        case 'inserted':
            return '#3f8600';
        case 'skip_existing':
        case 'duplicate':
            return '#d48806';
        case 'skip_unavailable':
        case 'skip_failed':
        case 'error':
            return '#cf1322';
        case 'quota':
            return '#d4380d';
        case 'done':
            return '#1677ff';
        default:
            return 'inherit';
    }
};

const YouTubeCopyPlaylist: React.FC = () => {
    const [source, setSource] = useState('');
    const [dest, setDest] = useState('');
    const [checking, setChecking] = useState(false);
    const [savingCode, setSavingCode] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [running, setRunning] = useState(false);
    const [authURL, setAuthURL] = useState('');
    const [authCode, setAuthCode] = useState('');
    const [check, setCheck] = useState<CheckResult | null>(null);
    const [preview, setPreview] = useState<PreviewResult | null>(null);
    const [logs, setLogs] = useState<LogLine[]>([]);
    const logEndRef = useRef<HTMLDivElement>(null);

    const keysOk = !!check && !check.auth_required && check.source?.readable && check.dest?.readable && check.dest_owned;

    const appendLog = (line: LogLine) => {
        setLogs((prev) => [...prev, line]);
        setTimeout(() => logEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 0);
    };

    const handleCheckKeys = async () => {
        setChecking(true);
        setPreview(null);
        try {
            const response = await axiosApiInstance.post(
                '/api/tools/youtube_copy_playlist/check',
                { source, dest },
                { skipErrorNotification: true }
            );
            const data = response.data as CheckResult;
            setCheck(data);
            if (data.auth_required) {
                message.warning(data.message || 'Destination channel is not authorized');
            } else {
                message.success(data.message || 'Keys OK');
            }
            appendLog({ type: 'info', message: data.message || 'Checked keys' });
        } catch (err) {
            message.error(apiError(err));
            appendLog({ type: 'error', message: apiError(err) });
        } finally {
            setChecking(false);
        }
    };

    const handleOpenLogin = async () => {
        try {
            const response = await axiosApiInstance.get('/api/tools/youtube_copy_playlist/auth/url', {
                skipErrorNotification: true,
            });
            const url = response.data?.auth_url as string;
            if (!url) {
                message.error('No authorization URL returned');
                return;
            }
            setAuthURL(url);
            window.open(url, '_blank');
            message.info('Complete Google login, then copy the full localhost URL from the address bar and paste it below.');
            appendLog({ type: 'info', message: 'Opened Google login for destination channel write access' });
        } catch (err) {
            message.error(apiError(err));
        }
    };

    const handleSaveCode = async () => {
        if (!authCode.trim()) {
            message.error('Paste the localhost URL from the address bar (http://localhost/?code=...)');
            return;
        }
        setSavingCode(true);
        try {
            const response = await axiosApiInstance.post(
                '/api/tools/youtube_copy_playlist/auth/code',
                { code: authCode.trim() },
                { skipErrorNotification: true }
            );
            message.success(response.data?.message || 'Write key saved');
            appendLog({
                type: 'info',
                message: `Saved write key for ${response.data?.channel?.title || 'channel'}`,
            });
            setAuthCode('');
            await handleCheckKeys();
        } catch (err) {
            message.error(apiError(err));
            appendLog({ type: 'error', message: apiError(err) });
        } finally {
            setSavingCode(false);
        }
    };

    const handlePreview = async () => {
        setPreviewing(true);
        try {
            const response = await axiosApiInstance.post(
                '/api/tools/youtube_copy_playlist/preview',
                { source, dest },
                { skipErrorNotification: true }
            );
            const data = response.data as PreviewResult;
            setPreview(data);
            appendLog({
                type: 'info',
                message: `Preview: ${data.plan.source_count} source, ${data.plan.dest_count} dest, ${data.plan.to_insert?.length || 0} to insert, ${data.plan.skipped_existing} already in dest, ${data.unavailable?.length || 0} unavailable`,
            });
            (data.plan.skipped_existing_ids || []).forEach((id) =>
                appendLog({ type: 'skip_existing', message: 'already in destination playlist', video_id: id })
            );
            (data.unavailable || []).forEach((u) =>
                appendLog({
                    type: 'skip_unavailable',
                    message: u.reason,
                    video_id: u.video_id || '(no video id)',
                    title: u.title,
                })
            );
            (data.duplicates || []).forEach((d) =>
                appendLog({
                    type: 'duplicate',
                    message: `${d.source_count}x in source, ${d.dest_count}x in dest`,
                    video_id: d.video_id,
                    title: d.title,
                })
            );
            (data.plan.to_insert || []).forEach((item) =>
                appendLog({ type: 'info', message: 'will insert', video_id: item.video_id, title: item.title })
            );
        } catch (err) {
            message.error(apiError(err));
            appendLog({ type: 'error', message: apiError(err) });
        } finally {
            setPreviewing(false);
        }
    };

    const handleCopy = async () => {
        setRunning(true);
        try {
            const token = await auth.currentUser?.getIdToken();
            const resp = await fetch(`${process.env.REACT_APP_API}/api/tools/youtube_copy_playlist/run`, {
                method: 'POST',
                headers: {
                    Accept: 'application/x-ndjson',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ source, dest }),
            });
            if (!resp.ok || !resp.body) {
                const text = await resp.text();
                let errMsg = text;
                try {
                    errMsg = JSON.parse(text).error || text;
                } catch {
                    // keep text
                }
                throw new Error(errMsg || `HTTP ${resp.status}`);
            }
            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buf = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const lines = buf.split('\n');
                buf = lines.pop() || '';
                for (const line of lines) {
                    if (!line.trim()) continue;
                    try {
                        const ev = JSON.parse(line) as LogLine & { result?: unknown };
                        appendLog(ev);
                    } catch {
                        appendLog({ type: 'info', message: line });
                    }
                }
            }
            if (buf.trim()) {
                try {
                    appendLog(JSON.parse(buf));
                } catch {
                    appendLog({ type: 'info', message: buf });
                }
            }
        } catch (err: any) {
            message.error(err?.message || 'Copy failed');
            appendLog({ type: 'error', message: err?.message || 'Copy failed' });
        } finally {
            setRunning(false);
        }
    };

    return (
        <PageContainer header={{ title: 'YouTube playlist copy', ghost: true }}>
            <Typography.Paragraph>
                Copy video IDs from one playlist into another you own. Videos stay on their original channels — nothing is reuploaded or removed from the source.
            </Typography.Paragraph>

            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Input
                    addonBefore="Source"
                    placeholder="https://www.youtube.com/playlist?list=PLsource..."
                    value={source}
                    onChange={(e) => {
                        setSource(e.target.value);
                        setCheck(null);
                        setPreview(null);
                    }}
                />
                <Input
                    addonBefore="Dest"
                    placeholder="https://www.youtube.com/playlist?list=PLdest..."
                    value={dest}
                    onChange={(e) => {
                        setDest(e.target.value);
                        setCheck(null);
                        setPreview(null);
                    }}
                />

                <Space wrap>
                    <Button
                        type="primary"
                        icon={<KeyOutlined />}
                        loading={checking}
                        disabled={!source.trim() || !dest.trim()}
                        onClick={handleCheckKeys}
                    >
                        Check keys
                    </Button>
                    <Button icon={<PlayCircleOutlined />} loading={previewing} disabled={!keysOk || running} onClick={handlePreview}>
                        Preview
                    </Button>
                    <Button
                        type="primary"
                        icon={<CopyOutlined />}
                        loading={running}
                        disabled={!keysOk || running}
                        onClick={handleCopy}
                    >
                        Copy videos
                    </Button>
                </Space>

                {check?.auth_required && (
                    <Alert
                        type="warning"
                        showIcon
                        message="Destination channel write key missing"
                        description={
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Typography.Text>
                                    {check.message ||
                                        'We do not have a write key for the destination playlist owner. Sign in as that channel, then paste the localhost URL from the address bar.'}
                                </Typography.Text>
                                <ol style={{ paddingLeft: 18, marginBottom: 0 }}>
                                    <li>Click Open Google login.</li>
                                    <li>Sign in as the <strong>destination</strong> channel owner (pick the Brand Account if prompted).</li>
                                    <li>Approve access. Google redirects to a localhost page that may look broken — that is expected.</li>
                                    <li>Copy the <strong>full URL</strong> from the address bar. It looks like <code>http://localhost/?state=state-token&amp;code=4/0ATs...</code></li>
                                    <li>Paste that URL below and click Save code, then Check keys again.</li>
                                </ol>
                                <Space wrap>
                                    <Button icon={<YoutubeOutlined />} onClick={handleOpenLogin}>
                                        Open Google login
                                    </Button>
                                    <Input
                                        style={{ minWidth: 360 }}
                                        placeholder="Paste http://localhost/?state=...&code=..."
                                        value={authCode}
                                        onChange={(e) => setAuthCode(e.target.value)}
                                    />
                                    <Button type="primary" loading={savingCode} onClick={handleSaveCode}>
                                        Save code
                                    </Button>
                                </Space>
                                {authURL && (
                                    <Typography.Paragraph copyable style={{ marginBottom: 0 }}>
                                        {authURL}
                                    </Typography.Paragraph>
                                )}
                            </Space>
                        }
                    />
                )}

                {keysOk && (
                    <Alert
                        type="success"
                        showIcon
                        icon={<CheckCircleOutlined />}
                        message={check.message}
                        description={
                            <Space direction="vertical">
                                <span>
                                    Source: {check.source.title} ({check.source.id}) on {check.source.channel_title}
                                </span>
                                <span>
                                    Dest: {check.dest.title} ({check.dest.id}) on {check.dest.channel_title}
                                </span>
                                {check.auth_channel && (
                                    <span>
                                        Authenticated as {check.auth_channel.title} ({check.auth_channel.id})
                                    </span>
                                )}
                            </Space>
                        }
                    />
                )}

                {preview && (
                    <Card size="small" title="Preview">
                        <Typography.Paragraph style={{ marginBottom: 8 }}>
                            Source items: {preview.plan.source_count} · Dest already: {preview.plan.dest_count} · To insert:{' '}
                            {preview.plan.to_insert?.length || 0} · Skipped (in dest): {preview.plan.skipped_existing} ·
                            Unavailable: {preview.unavailable?.length || 0} · Duplicate IDs: {preview.duplicates?.length || 0} (
                            {preview.duplicate_extra_entries} extra entries)
                        </Typography.Paragraph>
                    </Card>
                )}

                <Card size="small" title="Log">
                    <div
                        style={{
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                            fontSize: 12,
                            background: 'rgba(0,0,0,0.04)',
                            padding: 12,
                            minHeight: 180,
                            maxHeight: 420,
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {logs.length === 0 && (
                            <Typography.Text type="secondary">Logs appear here (skipped, inserted, unavailable, quota…)</Typography.Text>
                        )}
                        {logs.map((line, i) => (
                            <div key={i} style={{ color: logColor(line.type) }}>
                                {line.time ? `${line.time}  ` : ''}
                                [{line.type}] {line.video_id ? `${line.video_id}  ` : ''}
                                {line.message}
                                {line.title ? `  ${line.title}` : ''}
                            </div>
                        ))}
                        <div ref={logEndRef} />
                    </div>
                </Card>
            </Space>
        </PageContainer>
    );
};

export default YouTubeCopyPlaylist;
